// Copyright (c) Docugami, Inc. All rights reserved.

import { supportedExtensions } from "components/file_management/file_helpers";
import { Config } from "config";
import { ContentLoadState, ContentType, useDynamicHtmlContent } from "hooks/DynamicHtmlContent";
import { ProviderId } from "networking/StorageProviders";
import { useState } from "react";
import { useEffect, useRef } from "react";
import { TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { FilePickerProps, ItemTypeFilter, SelectionMode } from "./StorageProviderFilePicker";
import { ProviderPickerItem } from "./ProviderPickerItem";

interface DropboxPickerItem {
    id: string;
    name: string;
    isDir: boolean;
}

interface DropboxPickerProps extends FilePickerProps {
}

interface PickerState {
    isReady?: boolean;
    isError?: boolean;
}

export const DropboxPicker = (props: DropboxPickerProps): JSX.Element => {
    const { onCancel, onConfirm, token, selectionMode, itemTypeFilter } = props;
    const onConfirmRef = useRef<(items: ProviderPickerItem[]) => void>(onConfirm);
    const onCancelRef = useRef<() => void>(onCancel);

    const [state, setState] = useState<PickerState>({});

    const lifetimeRef = useRef<boolean>(true);

    const attributes = useRef({"data-app-key": Config.dropboxAppKey});
    const dropboxScript = useDynamicHtmlContent(ContentType.Script, "https://www.dropbox.com/static/api/2/dropins.js", "dropboxjs", attributes.current);

    useEffect(() => {
        onConfirmRef.current = onConfirm;
    }, [onConfirm]);

    useEffect(() => {
        onCancelRef.current = onCancel;
    }, [onCancel]);

    useEffect(() => {
        return () => {
            lifetimeRef.current = false;
            if (!!onCancelRef.current) {
                onCancelRef.current();
            }
        }
    }, []);

    useEffect(() => {
            setState({
                isReady: dropboxScript === ContentLoadState.Ready,
                isError: dropboxScript === ContentLoadState.Error
            });
    }, [dropboxScript]);

    useEffect(() => {
        if (!!state.isReady) {
            if (props.isOpen) {
                const options = {
                    success: (items: DropboxPickerItem[]) => {
                        const pickerItems = items.map<ProviderPickerItem>(x => {
                            return {
                                item: {
                                    id: x.id
                                },
                                path: x.name,
                                name: x.name,
                                isFolder: x.isDir,
                                providerId: ProviderId.Dropbox
                            }
                        });

                        onConfirmRef.current(pickerItems);
                    },
                    cancel: () => onCancelRef.current(),
                    multiselect: selectionMode === SelectionMode.Multiple,
                    folderselect: itemTypeFilter === ItemTypeFilter.All || itemTypeFilter === ItemTypeFilter.Folder,
                    extensions: supportedExtensions
                }

                // @ts-ignore
                Dropbox.choose(options);
            }
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.isOpen, state, token]);

    useEffect(() => {
        if (!!state.isError) {
            Telemetry.trackExceptionEvent(TelemetryExceptions.DropboxPickerLoadFailed);
            onCancel();
        }
    }, [state, onCancel]);

    return <></>
}
